/**
 * Structured JSON logger for server-side code.
 *
 * Every log line is a single JSON object so `docker logs | jq` works:
 *
 *   {"level":"error","message":"Failed to fetch","file":"api/search/route.ts","error":"ECONNREFUSED","timestamp":"2026-04-08T12:00:00.000Z"}
 *
 * On the client side (browser), falls back to plain console calls
 * so DevTools stays readable.
 */

const isServer = typeof window === 'undefined';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogPayload {
  /** Human-readable message */
  message: string;
  /** Source file path (e.g. "api/search/route.ts") */
  file?: string;
  /** Error message or stringified error */
  error?: unknown;
  /** Arbitrary structured context */
  [key: string]: unknown;
}

function formatError(err: unknown): string | undefined {
  if (err === undefined || err === null) return undefined;
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

function formatStack(err: unknown): string | undefined {
  if (err instanceof Error && err.stack) return err.stack;
  return undefined;
}

function emit(level: LogLevel, payload: LogPayload) {
  if (!isServer) {
    // Client-side: use regular console for DevTools readability
    const { message, ...rest } = payload;
    const args = Object.keys(rest).length > 0 ? [message, rest] : [message];
    switch (level) {
      case 'debug':
        console.debug(...args);
        break;
      case 'info':
        console.log(...args);
        break;
      case 'warn':
        console.warn(...args);
        break;
      case 'error':
        console.error(...args);
        break;
    }
    return;
  }

  // Server-side: structured JSON to stdout/stderr
  const { error: rawError, ...rest } = payload;
  const entry: Record<string, unknown> = {
    level,
    timestamp: new Date().toISOString(),
    ...rest,
  };
  if (rawError !== undefined) {
    entry.error = formatError(rawError);
    const stack = formatStack(rawError);
    if (stack) entry.stack = stack;
  }

  const line = JSON.stringify(entry);
  if (level === 'error') {
    process.stderr.write(line + '\n');
  } else {
    process.stdout.write(line + '\n');
  }
}

export const logger = {
  debug: (payload: LogPayload) => emit('debug', payload),
  info: (payload: LogPayload) => emit('info', payload),
  warn: (payload: LogPayload) => emit('warn', payload),
  error: (payload: LogPayload) => emit('error', payload),
};
